import { useState } from 'react';
import { Button, Chip, Notice } from './ui';
import { Icon } from '../lib/icons';
import { chooseDevice, refreshDevices, useAudioDevices, type AudioDevice } from '../lib/audioDevices';
import { connectHeadset, disconnectHeadset, setHeadsetEnabled, testHeadset, useHeadset } from '../lib/headset';

/**
 * Your settings → Headset (1.13.0, Teampace 3141): connect a headset whose call and mute buttons
 * work with Stitch (Jabra, Yealink, Poly and others with call buttons), test it, use it for the
 * sound of calls, or turn its buttons off. All of it on this browser only.
 */

/** Words a headset's sound devices are listed with (Poly's often still say Plantronics). */
function namesFor(name: string, brand: string | null): string[] {
  const words = [name.toLowerCase(), ...(brand ? [brand.toLowerCase()] : [])];
  if (brand === 'Poly' || brand === 'HP Poly') words.push('plantronics', 'poly');
  return words.filter(Boolean);
}
const matching = (list: AudioDevice[], words: string[]) => list.find((d) => words.some((w) => d.label.toLowerCase().includes(w)));

export function HeadsetSettings() {
  const h = useHeadset();
  const sound = useAudioDevices();
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState('');

  const words = h.device ? namesFor(h.device.name, h.device.brand) : [];
  const usedForSound = h.device && [sound.mic, sound.speaker].some((id) => [...sound.mics, ...sound.speakers].find((d) => d.id === id && words.some((w) => d.label.toLowerCase().includes(w))));

  async function useForSound() {
    setSaid('');
    const now = await refreshDevices();
    const mic = matching(now.mics, words);
    const speaker = matching(now.speakers, words);
    if (mic) chooseDevice('audioinput', mic.id);
    if (speaker) chooseDevice('audiooutput', speaker.id);
    setSaid(
      mic || speaker
        ? `Calls and Patches use ${h.device!.name} for ${mic && speaker ? 'the microphone and speaker' : mic ? 'the microphone' : 'the speaker'}.`
        : 'Stitch couldn’t find the headset among your microphones and speakers. Choose it during a call, under Sound.'
    );
  }

  return (
    <fieldset className="plain-fieldset">
      <legend className="field-label">Headset</legend>
      {!h.supported ? (
        <span className="hint">This browser can’t reach a headset’s buttons. Use Chrome or Edge, or the Stitch app for Mac or Windows, to answer, hang up and mute with your headset.</span>
      ) : h.device ? (
        <>
          <div className="headset-row">
            <Icon name="headset" size={18} />
            <strong>{h.device.name}</strong>
            {h.device.brand && <Chip small>{h.device.brand}</Chip>}
            <Button size="small" variant="ghost" onClick={() => void disconnectHeadset()}>
              Disconnect
            </Button>
          </div>
          <label className="check">
            <input type="checkbox" checked={h.enabled} onChange={(e) => setHeadsetEnabled(e.target.checked)} />
            <span className="cell-stack">
              <span>Use its call and mute buttons</span>
              <small className="muted">The call button answers and hangs up calls, Patches and meetings, and the mute button mutes. Its lights show when something rings and when you’re muted. Saved at once, on this computer.</small>
            </span>
          </label>
          <div className="headset-row">
            <Button size="small" variant="secondary" onClick={testHeadset}>
              Test
            </Button>
            <span className="small" aria-live="polite">
              {h.lastPress ? `Heard: ${h.lastPress}` : 'Its lights come on for 3 seconds. Press a button on it and Stitch says which.'}
            </span>
          </div>
          {!usedForSound && (
            <div className="headset-row">
              <Button size="small" variant="ghost" icon="volume" onClick={() => void useForSound()}>
                Use it for the sound of calls
              </Button>
            </div>
          )}
          {said && <span className="hint">{said}</span>}
          {!h.leading && <span className="hint">Another Stitch tab is using the headset. This tab takes over when that one closes.</span>}
        </>
      ) : (
        <>
          <span className="hint">Answer, hang up and mute with your headset’s own buttons: Jabra, Yealink, Poly (Plantronics) and other headsets with call buttons, plugged in or through their dongle.</span>
          <div className="headset-row">
            <Button
              size="small"
              variant="secondary"
              icon="headset"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void connectHeadset().finally(() => setBusy(false));
              }}
            >
              {busy ? 'Connecting…' : 'Connect a headset'}
            </Button>
          </div>
        </>
      )}
      {h.problem && <Notice tone="warn">{h.problem}</Notice>}
    </fieldset>
  );
}
